import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useFoods } from "@/hooks/useFoods";
import { useArticles } from "@/hooks/useArticles";
import { useBabies } from "@/hooks/useBabies";
import { useFoodLog } from "@/contexts/FoodLogContext";
import { Apple, BookOpen, Baby, Plus, NotebookPen } from "lucide-react";

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { foods } = useFoods();
  const { articles } = useArticles();
  const { babies } = useBabies();
  const { openQuickAdd } = useFoodLog();
  const [search, setSearch] = useState("");

  // Cmd/Ctrl + K opens
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const topFoods = useMemo(() => foods.slice(0, 8), [foods]);
  const topArticles = useMemo(() => articles.slice(0, 6), [articles]);

  const go = (path: string) => {
    onOpenChange(false);
    setSearch("");
    navigate(path);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Søk i mat, artikler, barn …"
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        <CommandEmpty>Ingen treff.</CommandEmpty>

        <CommandGroup heading="Hurtighandlinger">
          <CommandItem
            onSelect={() => {
              onOpenChange(false);
              openQuickAdd();
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Logg ny mat
          </CommandItem>
          <CommandItem onSelect={() => go("/matdagbok")}>
            <NotebookPen className="mr-2 h-4 w-4" />
            Åpne matdagbok
          </CommandItem>
          <CommandItem onSelect={() => go("/matkort")}>
            <Apple className="mr-2 h-4 w-4" />
            Matbibliotek
          </CommandItem>
        </CommandGroup>

        {babies.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Barn">
              {babies.map((b) => (
                <CommandItem key={b.id} onSelect={() => go(`/baby/${b.id}/diary`)}>
                  <Baby className="mr-2 h-4 w-4" />
                  {b.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {topFoods.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Matvarer">
              {topFoods.map((f) => (
                <CommandItem
                  key={f.id}
                  value={`food-${f.name}`}
                  onSelect={() => go(`/mat/${f.id}`)}
                >
                  <Apple className="mr-2 h-4 w-4" />
                  {f.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {topArticles.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Artikler">
              {topArticles.map((a) => (
                <CommandItem
                  key={a.id}
                  value={`article-${a.title}`}
                  onSelect={() => go(`/artikkel/${a.id}`)}
                >
                  <BookOpen className="mr-2 h-4 w-4" />
                  {a.title}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
